import apiClient from "../server";

const isValidObjectId = (v) => typeof v === 'string' && /^[0-9a-fA-F]{24}$/.test(v);

const sanitizeParams = (params = {}) => {
  const out = { ...params };
  if (out.cityId !== undefined && !isValidObjectId(out.cityId)) {
    delete out.cityId;
  }
  return out;
};

export const getAllNews = async (params = {}) => {
  try {
    const response = await apiClient.get(`news/backoffice`, { params: sanitizeParams(params) });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error getting news");
  }
};

export const createNews = async (data) => {
  try {
    const response = await apiClient.post(`news/backoffice`, data);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error creating news");
  }
};

export const deleteNews = async (id) => {
  try {
    const response = await apiClient.delete(`news/backoffice/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error deleting news");
  }
};

export const getAllProfiles = async (cityId) => {
  try {
    const cityParam = isValidObjectId(cityId) ? `&city=${cityId}` : '';
    const response = await apiClient.get(`profiles/all?pagination=false${cityParam}`);
    return response.data;
  } catch (error) {
    throw new Error("Error getting profiles");
  }
};

export const updateNews = async (id, data) => {
  try {
    const response = await apiClient.patch(`news/backoffice/update/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error updating news");
  }
};

export const updateProfileOrder = async (newsId, profileIds) => {
  try {
    const response = await apiClient.patch(`news/profiles/order/${newsId}`, { profileIds });
    return response.data;
  } catch (error) {
    throw new Error("Error updating profile order");
  }
};

export const toggleNewsEnabled = async (id, data) => {
  try {
    const response = await apiClient.patch(`news/backoffice/update/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error toggling news enabled");
  }
};

export const getNewsProfiles = async (newsId) => {
  try {
    const response = await apiClient.get(`news/profiles/${newsId}`);
    return response.data;
  } catch (error) {
    throw new Error("Error fetching news profiles");
  }
};

const getDataIds = (newsItem) => {
  if (Array.isArray(newsItem?.data)) {
    return newsItem.data.map(String);
  }
  if (Array.isArray(newsItem?.newsData)) {
    return newsItem.newsData
      .map((item) => item?._id || item?.id)
      .filter(Boolean)
      .map(String);
  }
  return [];
};

export const removeContentFromNews = async (newsId, contentId, currentNewsItem = null) => {
  try {
    let currentNews = currentNewsItem;
    if (!currentNews) {
      const all = await getAllNews({});
      currentNews = all?.data?.find((n) => n._id === newsId);
    }
    if (!currentNews) {
      throw new Error('News item not found');
    }

    const currentIds = getDataIds(currentNews);
    const updatedData = currentIds.filter((id) => String(id) !== String(contentId));

    const response = await apiClient.patch(`news/backoffice/update/${newsId}`, { data: updatedData });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error removing content from news");
  }
};

export const deleteProfileFromNews = (newsId, profileId, currentNewsItem = null) =>
  removeContentFromNews(newsId, profileId, currentNewsItem);

export const deletePublicationFromNews = (newsId, publicationId, currentNewsItem = null) =>
  removeContentFromNews(newsId, publicationId, currentNewsItem);

export const addContentToNews = async (newsId, contentId, currentNewsItem = null) => {
  try {
    if (!contentId) {
      throw new Error('No content ID provided');
    }

    let currentNews = currentNewsItem;
    if (!currentNews) {
      const all = await getAllNews({});
      currentNews = all?.data?.find((n) => n._id === newsId);
    }
    if (!currentNews) {
      throw new Error('News item not found');
    }

    const currentIds = getDataIds(currentNews);
    const updatedData = currentIds.includes(String(contentId))
      ? currentIds
      : [...currentIds, String(contentId)];

    const response = await apiClient.patch(`news/backoffice/update/${newsId}`, { data: updatedData });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error adding content to news");
  }
};

export const addNewsProfile = (newsId, body, currentNewsItem = null) => {
  const contentId = body.profileId || body.publicationId || body.id;
  return addContentToNews(newsId, contentId, currentNewsItem);
};

export const addNewsPublication = (newsId, body, currentNewsItem = null) => {
  const contentId = body.publicationId || body.profileId || body.id;
  return addContentToNews(newsId, contentId, currentNewsItem);
};

export const triggerAutoGeneration = async (cityId) => {
  try {
    const params = isValidObjectId(cityId) ? { cityId } : {};
    const response = await apiClient.post(`backoffice/news/auto-generate`, null, { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error triggering auto-generation");
  }
};

export const getAllMusicTypes = async (params = {}) => {
  try {
    const response = await apiClient.get(`params/music-types/all`, { params });
    return response.data;
  } catch (error) {
    throw new Error("Error getting music types");
  }
};

export const createMusicType = async (data) => {
  try {
    const response = await apiClient.post(`params/music-types/create`, data);
    return response.data;
  } catch (error) {
    throw new Error("Error creating music type");
  }
};

export const updateMusicType = async (id, data) => {
  try {
    const response = await apiClient.patch(`params/music-types/update/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error("Error updating music type");
  }
};

export const deleteMusicType = async (id) => {
  try {
    const response = await apiClient.delete(`params/music-types/delete/${id}`);
    return response.data;
  } catch (error) {
    throw new Error("Error deleting music type");
  }
};

export const getAllSubsections = async (params = {}) => {
  try {
    const response = await apiClient.get(`params/subSections/all`, { params });
    return response.data;
  } catch (error) {
    throw new Error("Error getting subsections");
  }
};

export const createSubsection = async (data) => {
  try {
    const response = await apiClient.post(`params/subSections/create`, data);
    return response.data;
  } catch (error) {
    throw new Error("Error creating subsection");
  }
};

export const updateSubsection = async (id, data) => {
  try {
    const response = await apiClient.patch(`params/subSections/update/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error("Error updating subsection");
  }
};

export const deleteSubsection = async (id) => {
  try {
    const response = await apiClient.delete(`params/subSections/delete/${id}`);
    return response.data;
  } catch (error) {
    throw new Error("Error deleting subsection");
  }
};
